import React from 'react';
import { ChatHelper, UserHelper } from '../helpers';
import { ChatMessage, ScreenType, UserRole } from '../types';

type MessageContentProps = {
  screenType: ScreenType;
  chatHelper: ChatHelper | null;
  message?: ChatMessage | null | undefined;
};

/** http(s) URLs + email addresses in plain text */
const URL_OR_EMAIL_SPLIT_REGEX = /(https?:\/\/[^\s<]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const renderContentWithLinks = (text: string): React.ReactNode => {
  const parts = text.split(URL_OR_EMAIL_SPLIT_REGEX);
  return (
    <>
      {parts.map((part, index) => {
        if (/^https?:\/\//i.test(part)) {
          return (
            <a key={index} href={part} target="_blank" rel="noopener noreferrer">
              {part}
            </a>
          );
        }
        if (EMAIL_REGEX.test(part)) {
          return (
            <a key={index} href={`mailto:${part}`}>
              {part}
            </a>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
};

const renderMessageBody = (content: string | null | undefined): React.ReactNode => {
  try {
    if (content === null || content === undefined) {
      return '';
    }

    if (typeof content === 'string') {
      return renderContentWithLinks(content);
    }

    if (typeof content === 'number' || typeof content === 'boolean') {
      return renderContentWithLinks(String(content));
    }

    return renderContentWithLinks(String(content));
  } catch (error) {
    console.error('Error rendering message content:', error);
    return 'Error displaying message';
  }
};

const MessageContent: React.FC<MessageContentProps> = ({
  screenType,
  chatHelper,
  message,
}) => {

  let className = '';
  if (screenType === ScreenType.Client) {
    className = message?.sender?.role === UserRole.Client ? 'chatyme-chat-sent' : 'chatyme-chat-received';
  } else if (screenType === ScreenType.Agent) {
    className = [...UserHelper.chatAgentUsers(), undefined].some(x => x === message?.sender?.role) ? 'chatyme-chat-sent' : 'chatyme-chat-received';
  }

  return (
    <React.Fragment>
      <div className={`chatyme-message-item chatyme-message-container ${className} ${chatHelper && message && [chatHelper.hasFlowMessage(message), chatHelper.firstFlowMessage(message), chatHelper.lastFlowMessage(message)].join(' ') || ''}`.trim()}>
        {message?.files?.map((file) => {
          const fileHref = file.signedUrl || file.url || '';
          return (
            <div key={file.id} className="chatyme-message-file-container">
              {fileHref ? (
                <a href={fileHref} target="_blank" rel="noopener noreferrer">
                  {file.name}
                </a>
              ) : (
                <span>{file.name}</span>
              )}
            </div>
          );
        })}
        {renderMessageBody(message?.content)}
      </div>
    </React.Fragment>
  );
};

export default MessageContent;

